import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { Action, FirmTypeLabel, PaginatedResponse } from '../../../common/models/common.model';
import { Supplier, SupplierCategory, SupplierListFilters, SUPPLIER_UNCATEGORISED } from '../../purchased-order.model';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription } from 'rxjs';
import { SupplierService } from '../../services/supplier.service';
import { SupplierCategoryService } from '../../services/supplier-category.service';

@Component({
  selector: 'app-suppliers',
  standalone: false,
  templateUrl: './suppliers.component.html',
  styleUrl: './suppliers.component.scss',
})
export class SuppliersComponent implements OnInit, OnDestroy {
  @ViewChild('addNewSupplierContent') addNewSupplierContent!: TemplateRef<any>;

  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;
  protected isSupplierDataLoading: boolean = false;
  protected modalRef!: NgbModalRef;
  protected suppliers: Supplier[] = [];
  protected supplierToBeEdit: any = null;
  protected totalCount: number = 0;
  protected currentPage: number = 1;
  protected searchTerm: string = '';

  /** Holds a category UUID, or SUPPLIER_UNCATEGORISED, or null for all. */
  protected selectedCategory: string | null = null;
  protected categoryOptions: { value: string; label: string }[] = [];

  private suppliersSubscription!: Subscription;
  private categorySubscription!: Subscription;
  private readonly searchTerms: Subject<string> = new Subject<string>();

  protected readonly Action = Action;
  protected readonly FirmTypeLabel = FirmTypeLabel;

  constructor(
    private modalService: NgbModal,
    private suppliersService: SupplierService,
    private supplierCategoryService: SupplierCategoryService
  ) {}

  ngOnInit(): void {
    this.getSuppliersList();
    this.loadCategoryOptions();

    // The list is paginated server-side, so searching has to be a request: filtering
    // locally would only ever look at the ten rows on screen.
    this.searchTerms.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.getSuppliersList();
    });
  }

  protected onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerms.next(term.trim());
  }

  protected clearSearch(): void {
    this.onSearchChange('');
  }

  protected onCategoryChange(): void {
    this.currentPage = 1;
    this.getSuppliersList();
  }

  protected get hasActiveFilters(): boolean {
    return !!(this.searchTerm.trim() || this.selectedCategory);
  }

  protected clearFilters(): void {
    this.searchTerm = '';
    this.selectedCategory = null;
    this.currentPage = 1;
    this.getSuppliersList();
  }

  /**
   * "Uncategorised" is the last option rather than a separate toggle: `category` and
   * `uncategorised` are mutually exclusive, and sending both returns nothing.
   */
  private loadCategoryOptions(): void {
    this.categorySubscription = this.supplierCategoryService.list().subscribe({
      next: (categories: SupplierCategory[]) => {
        this.categoryOptions = [
          ...(categories ?? []).map((category: SupplierCategory) => ({
            value: category.id,
            label: category.name,
          })),
          { value: SUPPLIER_UNCATEGORISED, label: 'Uncategorised' },
        ];
      },
      error: () => undefined,
    });
  }

  protected editSupplier(supplier: Supplier) {
    this.supplierToBeEdit = supplier;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'lg',
    };

    this._openModal(this.addNewSupplierContent, modalOptions);
  }

  protected onAddNewSupplier(): void {
    this.supplierToBeEdit = null;

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'lg',
    };

    this._openModal(this.addNewSupplierContent, modalOptions);
  }

  protected onSaveSupplierInfo(supplierInfo: any): void {
    this.currentPage = 1;
    this.getSuppliersList();
    this.modalRef?.dismiss();
  }

  protected deleteSupplier(supplier: Supplier) {
    const modalRef = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    // Set supplier-specific values
    modalRef.componentInstance.title = 'Delete Supplier';
    modalRef.componentInstance.message = 'Are you sure you want to delete this supplier?';
    modalRef.componentInstance.warning = 'This action cannot be undone.';

    modalRef.result.then(() => {
      this.suppliersSubscription = this.suppliersService.delete(supplier.id).subscribe({
        next: () => {
          this.suppliers = this.suppliers.filter(sup => sup.id !== supplier.id);
        },
      });
    });
  }

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this.modalService.open(content, options);
  }

  private getSuppliersList(): void {
    this.isSupplierDataLoading = true;

    const q: SupplierListFilters = {
      page: this.currentPage,
    };

    const search = this.searchTerm.trim();
    if (search) {
      q.search = search;
    }

    // The filter sends one or the other, never both.
    if (this.selectedCategory === SUPPLIER_UNCATEGORISED) {
      q.uncategorised = 'true';
    } else if (this.selectedCategory) {
      q.category = this.selectedCategory;
    }

    this.suppliersSubscription?.unsubscribe();
    this.suppliersSubscription = this.suppliersService
      .list(q)
      .pipe(finalize(() => (this.isSupplierDataLoading = false)))
      .subscribe({
        next: (suppliersListResponse: PaginatedResponse<Supplier>) => {
          this.suppliers = suppliersListResponse.results ?? [];
          this.hasNextPage = !!suppliersListResponse.next;
          this.hasPreviousPage = !!suppliersListResponse.previous;
          this.totalCount = suppliersListResponse.count;
        },
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getSuppliersList();
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
    this.suppliersSubscription?.unsubscribe();
    this.categorySubscription?.unsubscribe();
    this.searchTerms.complete();
  }
}
